<template>
  <div class="stripe-tab">
    <div class="input-container">
      <label for="name">Cardholder's Name</label>
      <input
        placeholder="Cardholder's Name"
        id="name"
        v-model="name"
        type="text"
      />
    </div>
    <div class="input-container">
      <label>Card Number</label>
      <div class="card-number-container">
        <input
          class="card-number"
          type="text"
          maxlength="19"
          placeholder="Card number"
          name="cardNumber"
          v-model="cardNumber"
          @keypress="validateNumber($event)"
        />
        <input
          type="card-expiry"
          maxlength="5"
          placeholder="MM/YY"
          name="cardExpiry"
          v-model="cardExpiry"
          @keypress="validateDates($event)"
        />
        <input
          type="card-cvv"
          maxlength="4"
          placeholder="CVC"
          name="cardCVV"
          v-model="cardCVV"
          @keypress="validateNumber($event)"
        />
      </div>
    </div>
    <div class="flex-container">
      <div class="input-container">
        <label for="country">Country</label>
        <select v-model="country">
          <option
            v-for="(countryName, countryCode) in isoCountries"
            :key="countryCode"
            :value="countryCode"
          >
            {{ countryName }}
          </option>
        </select>
      </div>
      <div class="input-container">
        <label for="postal">Postal Code</label>
        <input
          placeholder="Postal Code"
          id="postal"
          v-model="postal"
          type="text"
        />
      </div>
    </div>
    <!-- <div
      class="checkbox-container"
      @click="toggleSavePaymentMethod"
      v-if="!isSubscription"
    >
      <div class="checkbox" :class="{ active: savePaymentMethod }">
        <img src="@/assets/images/icons/check-white.svg" alt="save payment" />
      </div>
      Save payment method
    </div> -->
    <div class="error" v-if="stripeError">{{ stripeError }}</div>
    <div class="trial-ineligibility" v-if="trialIneligibility">
      You can still enjoy all of the benefits of our Pro Membership without a
      trial period. You will be charged [${{ subscriptionInitialPrice }}
      {{ subscriptionType }}].
    </div>
    <button class="submit-btn btn-red" @click="submit">
      <lottie
        v-if="stripeSubmitting"
        :options="{ animationData: authLoading }"
        :width="24"
        :height="24"
      />
      <span v-else>{{ btnCopy }}</span>
    </button>
    <div
      class="disclaimer"
      v-if="productType === availableProductTypes.COACH_SUBSCRIPTION"
    >
      By clicking "Proceed", you agree to enroll in our {{ billedIntervalAdjective }} subscription
      plan and to our Terms of Service. Your payment method will be charged the
      price above the first {{ billedInterval }} and {{ billedIntervalAdverb }} thereafter at the then-current
      rate. Prices are tax inclusive. Cancel any time in Settings. No refunds
      for partial unused periods.
    </div>
    <div
      class="disclaimer"
      v-else-if="productType === availableProductTypes.MEMBERSHIP_SUBSCRIPTION"
    >
      By clicking "{{ parsedBtnCopy }}", you agree to enroll in our {{ billedIntervalAdjective }}
      subscription plan and to our Terms of Service. Your payment method will be
      charged the price above the first {{ billedInterval }} and {{ billedIntervalAdverb }} thereafter at the
      then-current rate. Prices are tax inclusive. Cancel any time in Settings.
      No refunds for partial unused periods.
    </div>
    <div
      class="disclaimer"
      v-else-if="productType === availableProductTypes.MEMBERSHIP_TRIAL"
    >
      By clicking "{{ parsedBtnCopy }}" you agree to our
      <router-link :to="{ name: routerNames.TERMS }">
        Terms of Service
      </router-link>
      and will enroll in our {{ billedIntervalAdjective }} subscription plan unless you cancel during
      your
      {{ this.trialInitialInterval.interval_length }}-{{
        this.trialInitialInterval.interval_type
      }}
      free trial period. You will be charged ${{
        Math.ceil(trialRecurringPrice)
      }}
      the first {{ billedInterval }} and {{ billedIntervalAdverb }} thereafter at the then current rate. Cancel
      any time in Settings. No refunds for partial unused periods.
    </div>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from "vuex"
import ISO_COUNTRIES from "@/store/data/locale/iso-countries"
import Lottie from "vue-lottie"
import authLoading from "@/assets/animations/home/authLoading.json"
import moment from "moment"
import { ROUTER_NAMES } from "@/constants/router.constants"
import { PRODUCT_TYPE } from "@/constants/payment"
import {
  INTERVAL_TYPE_ADJECTIVES,
  INTERVAL_TYPE_ADVERBS,
} from "@/store/payment/payment.constants"
import { getSubscriptionProductStoreDetails } from "@/store/store/store.utils"

export default {
  components: {
    Lottie,
  },
  props: {
    productType: {
      type: String,
      default: PRODUCT_TYPE.POINT_PURCHASE,
    },
    promoCodes: {
      type: Array,
      default: () => {
        return []
      },
    },
    btnCopy: {
      type: String,
      default: "PROCEED",
    },
    redirectPath: {
      type: String,
      default: "/dashboard",
    },
  },
  data: function () {
    return {
      availableProductTypes: PRODUCT_TYPE,
      isoCountries: ISO_COUNTRIES,
      name: "",
      country: "US",
      postal: "",
      authLoading,
      cardNumber: "",
      cardExpiry: "",
      cardCVV: "",
      savePaymentMethod: false,
      routerNames: ROUTER_NAMES,
    }
  },
  computed: {
    ...mapGetters("Store", [
      "stripeError",
      "filledByStripe",
      "stripeSubmitting",
      "giftOrder",
      "stripeLast4",
      "subscribedItem",
      "subscriptionInitialPrice",
      "subscriptionType",
      "trialInitialInterval",
      "trialRecurringInterval",
      "trialRecurringPrice",
      "trialType",
      "selectedProduct",
    ]),
    ...mapGetters("PaymentLegacy", ["trialIneligibility"]),
    ...mapGetters("User", ["currentActiveGame"]),
    ...mapGetters("Store/Gift", ["giftForm"]),
    parsedBtnCopy () {
      const copy = this.btnCopy.toLowerCase()
      return copy[0].toUpperCase() + copy.slice(1)
    },
    isSubscription () {
      return [
        this.availableProductTypes.MEMBERSHIP_TRIAL,
        this.availableProductTypes.MEMBERSHIP_SUBSCRIPTION,
        this.availableProductTypes.COACH_SUBSCRIPTION,
      ].includes(this.productType)
    },
    promoCodesForCheckout () {
      return this.promoCodes.map(promoCode => {
        return promoCode.code
      })
    },
    subscriptionProductDetails () {
      return getSubscriptionProductStoreDetails(this.selectedProduct)
    },
    billedInterval () {
      return this.subscriptionProductDetails?.billedInterval
    },
    billedIntervalAdverb () {
      return INTERVAL_TYPE_ADVERBS[this.billedInterval]
    },
    billedIntervalAdjective () {
      return INTERVAL_TYPE_ADJECTIVES[this.billedInterval]
    },
  },
  methods: {
    ...mapActions("Store", [
      "loadStripe",
      "payWithStripe",
      "resetStripe",
      "subscribeWithStripe",
    ]),
    ...mapMutations("Store", ["setSelectedSavedCard"]),
    finishPayment () {
      if (this.productType === PRODUCT_TYPE.GIFT_PURCHASE) {
        // Gift Purchase
        this.$router.replace({
          name: ROUTER_NAMES.GIFT,
          query: {
            order_id: this.giftOrder.id,
            card_number: this.stripeLast4,
          },
        })
      } else if (PRODUCT_TYPE.COACH_SUBSCRIPTION === this.productType) {
        // Coach Subscription
        window.location.href = `/dashboard?payment_gateway=stripe&purchase_item=${this.subscribedItem}`
      } else if (this.$route.path === this.redirectPath) {
        // No redirection is requested
        this.$emit("extraAction")
      } else if (
        [PRODUCT_TYPE.MEMBERSHIP_SUBSCRIPTION].includes(this.productType)
      ) {
        // membership Subscription
        window.location.href = `/dashboard?payment_gateway=stripe&purchase_item=${this.subscribedItem}`
      } else if (this.productType === PRODUCT_TYPE.MEMBERSHIP_TRIAL) {
        // Trial Subscription

        window.location.href = `/dashboard?payment_gateway=stripe&purchase_item=${this.subscribedItem}`
      } else {
        window.location.href = this.redirectPath
      }
    },
    toggleSavePaymentMethod () {
      this.savePaymentMethod = !this.savePaymentMethod
    },
    async submit () {
      await this.setSelectedSavedCard(null)
      const month = this.cardExpiry.split("/")[0]
      let year = this.cardExpiry.split("/")[1]
      year = moment(year, "YY")
      year = year.isValid() ? year.format("YYYY") : null
      const body = new FormData()
      body.append("cardholder_name", this.name)
      body.append("number", this.cardNumber.replace(/\s/g, ""))
      body.append("cvc", this.cardCVV)
      body.append("expiry_month", month)
      body.append("expiry_year", year)
      body.append("country", this.country)
      body.append("postal_code", this.postal)
      body.append("save_payment_method", this.savePaymentMethod ? 1 : 0)
      if (this.isSubscription) {
        await this.subscribeWithStripe({
          paymentMethodBody: body,
          promoCodes: this.promoCodesForCheckout,
        })
      } else if (this.productType === PRODUCT_TYPE.GIFT_PURCHASE) {
        await this.payWithStripe({ body, orderType: "gift" })
      } else {
        await this.payWithStripe({ body })
      }
      if (this.filledByStripe) {
        this.finishPayment()
      }
    },
    validateNumber (evt) {
      const charCode = evt.which ? evt.which : evt.keyCode
      if (charCode >= 48 && charCode <= 57) {
        return true
      } else {
        evt.preventDefault()
      }
    },
    validateDates (evt) {
      const charCode = evt.which ? evt.which : evt.keyCode
      if (charCode >= 47 && charCode <= 57) {
        return true
      } else {
        evt.preventDefault()
      }
    },
  },
  watch: {
    cardNumber: function (newVal, oldVal) {
      if (newVal.length < oldVal.length) {
        return
      }
      if (newVal[newVal.length - 1] === " ") {
        return
      }
      const pureLength = newVal.replace(/\s/g, "").length
      if (pureLength === 16) {
        return
      }
      const cardTypeDecimal = newVal.slice(0, 2)
      if (
        cardTypeDecimal === "34" ||
        cardTypeDecimal === "36" ||
        cardTypeDecimal === "37"
      ) {
        console.log(pureLength)
        if (pureLength === 4 || pureLength === 10) {
          this.cardNumber = newVal + " "
        }
      } else {
        if (pureLength % 4 === 0) {
          this.cardNumber = newVal + " "
        }
      }
    },
    cardExpiry: function (newVal, oldVal) {
      if (newVal.length < oldVal.length) {
        if (oldVal.slice(oldVal.length - 1) === "/") {
          this.cardExpiry = newVal.slice(0, newVal.length - 1)
        }
        return
      }
      if (oldVal.length === 0 && Number(newVal) > 1) {
        this.cardExpiry = "0" + newVal + "/"
      } else if (oldVal.length === 1 && Number(newVal) > 12) {
        this.cardExpiry = oldVal
      } else if (newVal.length === 2) {
        this.cardExpiry = newVal + "/"
      }
    },
  },
  async created () {
    await this.resetStripe()
    this.loadStripe()
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/_mixins.scss";
@import "@/assets/style/_variables.scss";
.stripe-tab {
  color: #3b3b3b;
  padding: 0 4rem;
  @include phone-small {
    padding: 0 2rem;
  }
  .input-container {
    text-align: left;
    label {
      display: block;
      font-style: normal;
      font-weight: 600;
      font-size: 1.3rem;
      line-height: 1.6rem;
      margin: 0.9rem 0 1.3rem;
      @include phone-small {
        margin: 0.5rem 0 1rem;
      }
    }
    input,
    select {
      width: 100%;
      outline: none;
      border: 1px solid #dcdcdc;
      border-radius: 6px;
      padding: 1rem 1.1rem;
      font-style: normal;
      font-weight: normal;
      font-size: 1.6rem;
    }
    select {
      padding: 1rem 1.1rem;
      cursor: pointer;
    }
    .card-number-container {
      width: 100%;
      border-radius: 6px;
      border: 1px solid #dcdcdc;
      display: grid;
      // padding: 1rem 1.1rem;
      grid-template-columns: 4fr 2fr 1.1fr;
      @include phone-down {
        grid-template-columns: 4fr 2fr 1.3fr;
      }
      @include phone-small {
        grid-template-columns: 4fr 2fr 1.5fr;
      }
      input {
        border: none;
      }
    }
  }
  .checkbox-container {
    margin-top: 1.8rem;
    text-align: left;
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 1.6rem;
    color: #3b3b3b;
    .checkbox {
      cursor: pointer;
      width: 2.4rem;
      height: 2.4rem;
      border-radius: 0.6rem;
      border: 0.1rem solid #e2e8ee;
      margin-right: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      &.active {
        background: #000000;
      }
    }
  }
  .flex-container {
    display: flex;
    .input-container {
      width: 50%;
      &:first-child {
        margin-right: 1.3rem;
      }
    }
  }
  .error {
    background: rgba(250, 200, 206, 0.8);
    color: #db1d3f;
    font-style: normal;
    font-weight: 400;
    font-size: 1.3rem;
    padding: 1rem 1.6rem;
    border-radius: 4px;
    text-align: center;
    margin: 1rem 0;
  }
  .trial-ineligibility {
    color: #545460;
    font-weight: 300;
    font-size: 1.3rem;
    background: #f3f3f3;
    margin-top: 0.5rem;
    padding: 1rem;
  }
  .submit-btn {
    box-shadow: 0px 4px 30px rgba(244, 37, 86, 0.5);
    width: 100%;
    height: 5rem;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none;
    border: none;
    color: #ffffff;
    font-size: 1.8rem;
    line-height: 2.8rem;
    margin: 2rem 0 0;
    cursor: pointer;
    font-weight: bold;
    font-family: $font-poppins;
    text-transform: capitalize;
    @include tablet-down {
      margin: 2.3rem auto 0;
    }
  }
  .disclaimer {
    margin-top: 1.7rem;
    font-weight: 300;
    color: #545460;
    font-size: 1.2rem;
    text-align: left;
    a {
      text-decoration: underline;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/payment_v2/StripeTab.vue